import { Service } from "typedi";
import { config } from "../../Config";
import { AgentEnrollmentResponse } from "../../../../schools-backend/src/agent/application/payloads/AgentEnrollmentResponse";
import { RPCMessage } from "../../../shared/RPC/RPCMessage";
import { WS_CLOSE_AGENT_NOT_FOUND, WS_CLOSE_INVALID_TOKEN } from "../../../shared/WebSocketCloseCodes";
import { ServerSession } from "./ServerSession";
import { StudentEmailManager } from "./StudentEmailManager";

const TOKEN_STORAGE_KEY = "zaindari.authToken";
const ENROLLMENT_TOKEN_STORAGE_KEY = "zaindari.enrollmentToken";
const RECONNECT_DELAY_MS = 1000;

/**
 * Browser-iframe counterpart of the chrome agent's AgentConnector.
 *
 * Differences:
 *   - `chrome.storage.local` → `localStorage` (shared across all instances on
 *     the controller origin).
 *   - Enrollment token is seeded from Config OR the `?enrollmentToken=...`
 *     query param injected by the appliance into controller.html.
 *   - Calls `{enrollmentEndpoint}/network/enroll` (v1 hardcodes the agentType
 *     in the URL path, matching the backend convention).
 */
@Service()
export class AgentConnector {
    private ws: WebSocket | null = null;

    constructor(
        private readonly serverSession: ServerSession,
        private readonly studentEmailManager: StudentEmailManager
    ) {}

    async connect(): Promise<void> {
        const token = await this.getAuthenticationTokenWithRetry();
        const email = await this.getStudentEmailWithRetry();

        const webSocketUrl = new URL(config.WSEndpoint);
        webSocketUrl.searchParams.set("token", token);
        webSocketUrl.searchParams.set("email", email);

        console.log("[agent-connector] connecting to", webSocketUrl.toString());
        const ws = new WebSocket(webSocketUrl.toString());
        this.ws = ws;

        ws.addEventListener("open", () => {
            const send = (message: RPCMessage) => ws.send(JSON.stringify(message));
            const disconnect = () => ws.close(1000, "Client disconnected");
            void this.serverSession.start(send, disconnect);
        });

        ws.addEventListener("message", async (event) => {
            try {
                const data = typeof event.data === "string" ? event.data : await (event.data as Blob).text();
                await this.serverSession.handleMessage(JSON.parse(data));
            } catch (err) {
                console.error("[agent-connector] failed to parse message", err);
            }
        });

        ws.addEventListener("close", async (event) => {
            console.log("[agent-connector] ws closed", event.code, event.reason);
            await this.serverSession.close();

            if (event.code === WS_CLOSE_INVALID_TOKEN || event.code === WS_CLOSE_AGENT_NOT_FOUND) {
                console.warn("[agent-connector] token rejected, clearing for re-enrollment");
                localStorage.removeItem(TOKEN_STORAGE_KEY);
            }

            setTimeout(() => {
                void this.connect();
            }, RECONNECT_DELAY_MS);
        });

        ws.addEventListener("error", (err) => {
            console.error("[agent-connector] ws error", err);
        });
    }

    disconnect(): void {
        this.ws?.close(1000, "Leadership lost");
        this.ws = null;
    }

    private async getAuthenticationTokenWithRetry(delayMs = RECONNECT_DELAY_MS): Promise<string> {
        while (true) {
            try {
                const token = localStorage.getItem(TOKEN_STORAGE_KEY);
                if (token) return token;

                console.log("[agent-connector] no auth token, enrolling…");
                await this.enroll();
            } catch (err) {
                console.warn("[agent-connector] enrollment attempt failed", err);
            }
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
    }

    private async getStudentEmailWithRetry(delayMs = RECONNECT_DELAY_MS): Promise<string> {
        while (true) {
            const email = await this.studentEmailManager.getEmail();
            if (email) return email;
            console.log("[agent-connector] student email not available, retrying");
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
    }

    private async enroll(): Promise<void> {
        const enrollmentToken = this.readEnrollmentToken();
        if (!enrollmentToken) {
            throw new Error("Enrollment token not found");
        }

        const response = await fetch(config.enrollmentEndpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${enrollmentToken}`
            }
        });
        if (!response.ok) {
            throw new Error(`Enrollment failed with status ${response.status}`);
        }

        const body = (await response.json()) as AgentEnrollmentResponse;
        if (!body.token) {
            throw new Error("Enrollment response did not contain token");
        }

        localStorage.setItem(TOKEN_STORAGE_KEY, body.token);
    }

    private readEnrollmentToken(): string | null {
        // Order of preference:
        //   1. ?enrollmentToken=... in the iframe URL — future appliance path,
        //      where the MITM rewrites controller.html for each client.
        //   2. config.enrollmentToken — baked at build time per flavour/school,
        //      matching how the chrome/windows agents ship today.
        //   3. localStorage — last-used token from a prior session.
        try {
            const fromQuery = new URL(location.href).searchParams.get("enrollmentToken");
            if (fromQuery) {
                localStorage.setItem(ENROLLMENT_TOKEN_STORAGE_KEY, fromQuery);
                return fromQuery;
            }
        } catch {
            // ignore
        }
        if (config.enrollmentToken) {
            return config.enrollmentToken;
        }
        return localStorage.getItem(ENROLLMENT_TOKEN_STORAGE_KEY);
    }
}
